import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGameStore } from '../../store/gameStore';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui/button';
import { 
  Home, 
  Users, 
  MapPin, 
  ClipboardList, 
  Target,
  Activity,
  FileText,
  ChevronLeft,
  ChevronRight,
  Trophy,
  BarChart3,
  Crosshair,
  LogOut,
  Settings2,
  GitBranch,
  MessageSquare,
  BookOpen,
  Zap
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { BrandLogo } from '../shared/BrandLogo';

const NAV_ITEMS = [
  { path: '/dashboard', icon: Home, label: 'Home' },
  { path: '/lineup', icon: Users, label: 'Lineup' },
  { path: '/game-setup', icon: Settings2, label: 'Game Setup' },
  { path: '/game-mode', icon: Zap, label: 'Game Mode' },
  { path: '/pitching', icon: Target, label: 'Pitchers & Counts' },
  { path: '/game-summary', icon: Trophy, label: 'Summary' },
  { path: '/stats', icon: Activity, label: 'Stats' },
  { path: '/season-stats', icon: BarChart3, label: 'Season' },
  { path: '/spray-charts', icon: Crosshair, label: 'Spray Charts' },
  { path: '/messages', icon: MessageSquare, label: 'Messages' },
  { path: '/key', icon: BookOpen, label: 'Key' },
];

const BOTTOM_NAV_ITEMS = [
  { path: '/pregame', icon: FileText, label: 'Pre-Game Sheet' },
  { path: '/tournaments', icon: GitBranch, label: 'Tournaments' },
];

export const GameSidebar = ({ collapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentGameId, currentTeamId } = useGameStore();
  const { user, logout } = useAuthStore();

  const handleNavigation = (path) => {
    // Allow navigation to dashboard and tournaments without game selected
    if (path === '/dashboard' || path === '/tournaments' || (currentGameId && currentTeamId)) {
      navigate(path);
    }
  };

  return (
    <div 
      className={cn(
        "fixed left-0 top-0 h-full shadow-xl z-40 transition-all duration-300 flex flex-col",
        collapsed ? "w-14" : "w-48"
      )}
      style={{
        background: 'linear-gradient(180deg, #0A1322 0%, #131E36 100%)',
        borderRight: '1px solid rgba(212, 160, 58, 0.2)',
      }}
      data-testid="game-sidebar"
    >
      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 text-white rounded-full p-1 shadow-lg hover:scale-110 transition-transform"
        style={{ background: '#1FA85A' }}
        data-testid="sidebar-toggle"
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      {/* Logo — official 3D DS monogram + emerald-gold wordmark */}
      <div className="p-3" style={{ borderBottom: '1px solid rgba(212, 160, 58, 0.2)' }}>
        {collapsed ? (
          <div className="flex justify-center">
            <BrandLogo size={32} />
          </div>
        ) : (
          <div className="flex items-center gap-2 justify-center">
            <BrandLogo size={32} />
            <span className="font-extrabold text-xs tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.5px' }}>
              <span style={{ color: '#2EBE6B' }}>DIAMOND</span>
              <span style={{ color: '#F5C455' }} className="ml-1">SCORER</span>
            </span>
          </div>
        )}
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 py-4">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          const isDisabled = item.path !== '/dashboard' && (!currentGameId || !currentTeamId);

          return (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              disabled={isDisabled}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-3 transition-all",
                isActive 
                  ? "bg-ds-emerald/20 text-white border-l-4 border-ds-gold-bright"
                  : "text-slate-400 hover:bg-white/5 hover:text-white border-l-4 border-transparent",
                isDisabled && "opacity-50 cursor-not-allowed"
              )}
            >
              <Icon size={20} className="flex-shrink-0" />
              {!collapsed && (
                <span className="text-sm font-medium truncate">{item.label}</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User & Logout */}
      <div className="mt-auto" style={{ borderTop: '1px solid rgba(212, 160, 58, 0.2)' }}>
        {/* Bottom nav items (Pre-Game Sheet) */}
        {BOTTOM_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          const isDisabled = !currentGameId || !currentTeamId;
          return (
            <button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              disabled={isDisabled}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 transition-all",
                isActive 
                  ? "bg-ds-emerald/20 text-white border-l-4 border-ds-gold-bright"
                  : "text-slate-500 hover:bg-white/5 hover:text-white border-l-4 border-transparent",
                isDisabled && "opacity-50 cursor-not-allowed"
              )}
            >
              <Icon size={18} className="flex-shrink-0" />
              {!collapsed && (
                <span className="text-xs font-medium truncate">{item.label}</span>
              )}
            </button>
          );
        })}
        {currentGameId && !collapsed && (
          <div className="px-3 py-2 text-xs" style={{ color: '#2EBE6B' }}>Game active</div>
        )}
        <button
          onClick={() => { logout(); window.location.href = '/'; }}
          data-testid="sidebar-logout-btn"
          className="w-full flex items-center gap-3 px-3 py-3 text-slate-400 hover:bg-red-900/40 hover:text-red-300 transition-all"
        >
          <LogOut size={20} className="flex-shrink-0" />
          {!collapsed && (
            <span className="text-sm font-medium truncate">{user?.username || 'Logout'}</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default GameSidebar;
